import React, { useState, useEffect } from "react";
import UserScreenWindow from "../User/Components/UserScreenWindow";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FaTools,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaPaperPlane,
  FaClock,
  FaWrench,
  FaExclamationCircle,
  FaTimes,
  FaCheck,
  FaArrowRight,
  FaHardHat,
  FaPlus,
  FaSearch,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountUp,
  FaChevronLeft,
  FaUsers,
} from "react-icons/fa";
import { getAllDocsSnap } from "../../utils/firebase/Firebase.utils";
import PhotoCarousel from "../User/Marketplace/PhotoCarousel";
import ServiceRequestDetails from "../Dashboard/PropertyManagement/ServiceRequestDetails";

function ServiceRequestsMobile() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [activeFilter, setActiveFilter] = useState("All");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Check if user has security role
  const hasSecurityRole = currentUser.roles?.includes("security");

  useEffect(() => {
    const fetchServiceRequests = async () => {
      await getAllDocsSnap("serviceRequests", (docs) => {
        // Sort by creation date
        docs.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA; // Newest first by default
        });
        setServiceRequests(docs);
        setFilteredRequests(docs);
        setIsLoading(false);
      });
    };
    fetchServiceRequests();
  }, []);

  useEffect(() => {
    // Apply search filter and sorting
    let filtered = [...serviceRequests];

    // Apply status filter
    if (activeFilter !== "All") {
      filtered = filtered.filter((request) => request.status === activeFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((request) => {
        // Search in service type
        if (request.serviceType?.toLowerCase().includes(term)) return true;
        // Search in description
        if (request.description?.toLowerCase().includes(term)) return true;
        // Search in unit identifier
        if (request.identifier?.toLowerCase().includes(term)) return true;
        // Search in user name
        if (request.userName?.toLowerCase().includes(term)) return true;
        return false;
      });
    }

    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });

    setFilteredRequests(filtered);
  }, [searchQuery, serviceRequests, sortOrder, activeFilter]);

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "newest" ? "oldest" : "newest");
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedRequest(null);
  };

  const getStatusCount = (status) => {
    if (status === "All") {
      return serviceRequests.length;
    }
    return serviceRequests.filter((request) => request.status === status)
      .length;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  // Status styles and icons
  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "#FFA500"; // Orange
      case "Scheduled":
        return "#4299e1"; // Blue
      case "In Progress":
        return "#9f7aea"; // Purple
      case "Completed":
        return "#8FCD00"; // Green
      case "Cancelled":
        return "#ef4444"; // Red
      default:
        return "#808080"; // Gray
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Pending":
        return <FaClock />;
      case "Scheduled":
        return <FaCalendarAlt />;
      case "In Progress":
        return <FaWrench />;
      case "Completed":
        return <FaCheck />;
      case "Cancelled":
        return <FaTimes />;
      default:
        return <FaClock />;
    }
  };

  const filters = [
    "All",
    "Pending",
    "Scheduled",
    "In Progress",
    "Completed",
    "Cancelled",
  ];

  const renderRequestDetails = () => {
    if (!selectedRequest) return null;

    // If user has security role, use ServiceRequestDetails component
    if (hasSecurityRole) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ServiceRequestDetails
            request={selectedRequest}
            onClose={handleCloseDetails}
            setSelectedRequest={setSelectedRequest}
          />
        </div>
      );
    }

    // For non-security users, use the original detail view
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-[#363062] rounded-lg">
                <FaTools className="text-white text-sm" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#363062]">
                  Service Request Details
                </h3>
                <p className="text-xs text-gray-500">
                  Ref: {selectedRequest.id}
                </p>
              </div>
            </div>
            <button
              onClick={handleCloseDetails}
              className="text-gray-500 hover:text-gray-700"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          {/* Status Badge */}
          <div className="p-4 border-b border-gray-200">
            <div
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium gap-1.5"
              style={{
                backgroundColor: `${getStatusColor(selectedRequest.status)}15`,
                color: getStatusColor(selectedRequest.status),
              }}
            >
              {getStatusIcon(selectedRequest.status)}
              <span>{selectedRequest.status}</span>
            </div>
          </div>

          {/* Content Grid */}
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">Unit</div>
              <div className="text-sm font-medium text-[#363062]">
                {selectedRequest.identifier}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">
                Service Type
              </div>
              <div className="text-sm">{selectedRequest.serviceType}</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">
                Date Submitted
              </div>
              <div className="text-sm">
                {formatDate(selectedRequest.createdAt)}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">
                Preferred Date & Time
              </div>
              <div className="text-sm">
                {formatDate(selectedRequest.preferredDate)} |{" "}
                {selectedRequest.preferredTime}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">
                Requested By
              </div>
              <div className="text-sm">
                {selectedRequest.userName} ({selectedRequest.userEmail})
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="p-4 border-t border-gray-100">
            <div className="text-xs font-medium text-gray-500 mb-2">
              Description
            </div>
            <div className="p-3 bg-gray-50 rounded-lg text-xs">
              {selectedRequest.description}
            </div>
          </div>

          {/* Image (if available) */}
          {selectedRequest.image && (
            <div className="p-4 border-t border-gray-100">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Attached Image
              </div>
              <div className="rounded-lg overflow-hidden border border-gray-100">
                <PhotoCarousel images={[selectedRequest.image]} />
              </div>
            </div>
          )}

          {/* Assigned To */}
          <div className="p-4 border-t border-gray-100">
            <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5">
              <FaUsers className="text-gray-400" />
              <span>Assigned Staff</span>
            </div>
            {selectedRequest.assignedTo &&
            selectedRequest.assignedTo.length > 0 ? (
              <div className="space-y-2">
                {selectedRequest.assignedTo.map((staff, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                  >
                    <div className="w-6 h-6 bg-[#363062] rounded-full flex items-center justify-center text-white text-xs">
                      {staff.label?.charAt(0) || "U"}
                    </div>
                    <div className="text-xs font-medium">{staff.label}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic">
                Not yet assigned
              </div>
            )}
          </div>

          {/* Comments */}
          {selectedRequest.comments && selectedRequest.comments.length > 0 && (
            <div className="p-4 border-t border-gray-100">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Comments
              </div>
              <div className="space-y-3">
                {selectedRequest.comments.map((comment, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 bg-[#363062] rounded-full flex items-center justify-center text-white text-xs flex-shrink-0 mt-0.5">
                        {comment.createdBy?.charAt(0) || "U"}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div className="text-xs font-medium">
                            {comment.createdBy}
                          </div>
                          <div className="text-xs text-gray-500">
                            {comment.createdAt}
                          </div>
                        </div>
                        <div className="text-xs mt-1">{comment.message}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resolution */}
          {selectedRequest.resolution && (
            <div className="p-4 border-t border-gray-100">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Resolution
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-xs">
                {selectedRequest.resolution}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleCloseDetails}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-xs font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <UserScreenWindow label="Service Requests" icon="service-requests">
      <div className="w-full mx-auto px-4 max-w-4xl">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaTools className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Service Requests
              </h1>
              <p className="text-xs text-gray-500">
                View and manage tenant service requests
              </p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaSearch className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
            placeholder="Search by unit, service type, or description..."
            value={searchQuery}
            onChange={handleSearch}
            style={{ borderColor: "#E2E8F0" }}
          />
          {searchQuery && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setSearchQuery("")}
            >
              <FaTimes className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Filter Controls */}
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={toggleSortOrder}
            className="px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap border border-gray-200 bg-white text-[#363062] hover:bg-gray-50"
            title={sortOrder === "newest" ? "Newest first" : "Oldest first"}
          >
            {sortOrder === "newest" ? (
              <div className="flex items-center gap-1">
                <FaSortAmountDown className="w-3 h-3" />
                <span>Newest</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <FaSortAmountUp className="w-3 h-3" />
                <span>Oldest</span>
              </div>
            )}
          </button>
        </div>

        {/* Status Filter Buttons */}
        <div className="mb-6 -mx-4 relative">
          <div className="overflow-x-auto px-4 pr-20 flex no-scrollbar relative">
            <div className="flex space-x-2">
              {filters.map((filter, index) => (
                <button
                  key={index}
                  className={`flex items-center whitespace-nowrap px-4 py-2 rounded-full text-xs font-medium transition-all ${
                    activeFilter === filter
                      ? "bg-[#363062] text-white shadow-sm"
                      : "bg-gray-50 text-[#363062] hover:bg-[#363062]/5"
                  }`}
                  onClick={() => setActiveFilter(filter)}
                >
                  {filter === "Pending" && (
                    <FaClock className="mr-1.5 text-xs" />
                  )}
                  {filter === "Scheduled" && (
                    <FaCalendarAlt className="mr-1.5 text-xs" />
                  )}
                  {filter === "In Progress" && (
                    <FaWrench className="mr-1.5 text-xs" />
                  )}
                  {filter === "Completed" && (
                    <FaCheck className="mr-1.5 text-xs" />
                  )}
                  {filter === "Cancelled" && (
                    <FaTimes className="mr-1.5 text-xs" />
                  )}
                  {filter === "All" && <FaFilter className="mr-1.5 text-xs" />}
                  <span>{filter}</span> ({getStatusCount(filter)})
                </button>
              ))}
            </div>
          </div>
          <div className="absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-gray-100 via-gray-100 to-transparent pointer-events-none"></div>
        </div>

        {/* Service Requests List */}
        <div className="mt-4 space-y-3 pb-20">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
                <p className="mt-2 text-sm text-[#363062]">
                  Loading requests...
                </p>
              </div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FaTools className="text-[#363062] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                {serviceRequests.length === 0
                  ? "No Service Requests"
                  : "No Matching Results"}
              </h3>
              <p className="text-xs text-gray-500">
                {serviceRequests.length === 0
                  ? "No service requests have been submitted yet"
                  : activeFilter !== "All"
                  ? `No ${activeFilter} requests found`
                  : "Try adjusting your search terms"}
              </p>
            </div>
          ) : (
            filteredRequests.map((request) => (
              <div
                key={request.id}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md hover:translate-y-[-2px] cursor-pointer"
                onClick={() => handleViewDetails(request)}
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-[#363062]">
                      {request.identifier}
                    </div>
                    <div
                      className="flex items-center px-2 py-1 rounded-full text-xs font-medium gap-1.5"
                      style={{
                        backgroundColor: `${getStatusColor(request.status)}15`,
                        color: getStatusColor(request.status),
                      }}
                    >
                      <span className="text-xs">
                        {getStatusIcon(request.status)}
                      </span>
                      {request.status || "Unknown"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                    <div className="flex items-center gap-1.5">
                      <FaWrench className="text-gray-400 flex-shrink-0" />
                      <span className="font-medium">{request.serviceType}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaCalendarAlt className="text-gray-400 flex-shrink-0" />
                      <span>{formatDate(request.preferredDate)}</span>
                    </div>
                  </div>

                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-1.5">
                      Description:
                    </div>
                    <div className="text-xs line-clamp-2">
                      {request.description}
                    </div>
                  </div>

                  {request.image && (
                    <div className="mt-3">
                      <div className="w-full h-24 rounded-lg overflow-hidden">
                        <img
                          src={request.image}
                          alt="Request"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center py-2 px-4 bg-gray-50 border-t border-gray-100">
                  <div className="text-xs text-gray-500">
                    {formatDate(request.createdAt)}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-[#363062]">
                    <FaUsers className="text-xs" />
                    {request.userName}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {showDetails && renderRequestDetails()}
    </UserScreenWindow>
  );
}

export default ServiceRequestsMobile;
